/**
 * Jyotish Tarang — Kundali Milan (8-Koot Compatibility) v1.6
 * Pro tier | With PDF download
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BuyCreditModal from '../components/BuyCreditModal';

function getToken() {
  return sessionStorage.getItem('access_token') || localStorage.getItem('access_token') || '';
}
function apiFetch(url: string, options?: RequestInit) {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers: { ...headers, ...(options?.headers || {}) } });
}
async function downloadFile(url: string, filename: string) {
  const token = getToken();
  const resp  = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
  const blob  = await resp.blob();
  const a     = document.createElement('a');
  a.href      = URL.createObjectURL(blob);
  a.download  = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

interface Chart { id: number; name: string; dob: string; place_name: string; }
interface MilanResult {
  person1: string; person2: string;
  total_score: number; max_score: number; percentage: number; verdict: string;
  koots: Record<string, any>;
  mangal_dosha: { person1: boolean; person2: boolean; concern: boolean; note: string; };
  narrative?: string | null;
  doshas?: Array<{ name: string; severity: string; remedy: string; }>;
}

const KOOT_ORDER = ['varna','vashya','tara','yoni','graha_maitri','gana','bhakoot','nadi'];

export default function KundaliMilan() {
  const navigate  = useNavigate();
  const { t } = useTranslation();
  const [charts,   setCharts]   = useState<Chart[]>([]);
  const [chart1,   setChart1]   = useState<number | ''>('');
  const [chart2,   setChart2]   = useState<number | ''>('');
  const [result,   setResult]   = useState<MilanResult | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl,   setPdfUrl]   = useState('');
  const [pdfName,  setPdfName]  = useState('');
  const [error,    setError]    = useState('');
  const [showBuyMilan, setShowBuyMilan] = useState(false);

  const KOOT_LABELS: Record<string, string> = {
    varna: t('milan.koots.varna.label'), vashya: t('milan.koots.vashya.label'),
    tara: t('milan.koots.tara.label'), yoni: t('milan.koots.yoni.label'),
    graha_maitri: t('milan.koots.grahaMaitri.label'), gana: t('milan.koots.gana.label'),
    bhakoot: t('milan.koots.bhakoot.label'), nadi: t('milan.koots.nadi.label'),
  };
  const KOOT_MEANING: Record<string, string> = {
    varna: t('milan.koots.varna.meaning'), vashya: t('milan.koots.vashya.meaning'),
    tara: t('milan.koots.tara.meaning'), yoni: t('milan.koots.yoni.meaning'),
    graha_maitri: t('milan.koots.grahaMaitri.meaning'), gana: t('milan.koots.gana.meaning'),
    bhakoot: t('milan.koots.bhakoot.meaning'), nadi: t('milan.koots.nadi.meaning'),
  };

  useEffect(() => {
    apiFetch('/api/charts/')
      .then(r => r.json())
      .then(d => setCharts(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  async function handleCompute() {
    if (!chart1 || !chart2)  { setError(t('milan.errors.selectBoth')); return; }
    if (chart1 === chart2)   { setError(t('milan.errors.selectDifferent')); return; }
    setLoading(true); setError(''); setResult(null); setPdfUrl(''); setPdfName('');
    try {
      const r = await apiFetch('/api/charts/compatibility', {
        method: 'POST', body: JSON.stringify({ chart1_id: chart1, chart2_id: chart2 }),
      });
      const data = await r.json();
      if (!r.ok) {
        // Phase 5 (14 Jul 2026, one-time-payments): a 402 with credit_type
        // means the tier/free-monthly-allowance was exhausted (or this is
        // Free tier, credit-only per decision 3) but a ₹99 purchase can
        // still unlock this one Milan check -- offer BuyCreditModal instead
        // of a dead-end error. See charts.py::compatibility()'s gate_feature()
        // call (Phase 3, v4.2.2).
        if (data.credit_type === 'milan') { setShowBuyMilan(true); return; }
        setError(data.error || t('milan.errors.checkFailed'));
        return;
      }
      setResult(data);
    } catch { setError(t('common.networkError')); }
    finally { setLoading(false); }
  }

  async function handleMilanPurchased() {
    setShowBuyMilan(false);
    await handleCompute();
  }

  async function handleGeneratePDF() {
    if (!chart1 || !chart2) return;
    setPdfLoading(true);
    try {
      const r = await apiFetch('/api/charts/compatibility/pdf', {
        method: 'POST', body: JSON.stringify({ chart1_id: chart1, chart2_id: chart2 }),
      });
      const data = await r.json();
      if (!r.ok) { alert(data.error || t('milan.errors.pdfFailed')); return; }
      setPdfUrl(data.download_url);
      setPdfName(data.filename);
    } catch { alert(t('milan.errors.pdfFailedRetry')); }
    finally { setPdfLoading(false); }
  }

  function scoreColor(score: number, max: number): string {
    const pct = score / max;
    return pct >= 0.75 ? 'var(--teal)' : pct >= 0.5 ? 'var(--sfb)' : 'var(--red)';
  }

  // Phase 5 (14 Jul 2026, one-time-payments, decision 3): this used to be a
  // hard wall for Free tier ("Upgrade to Pro"), with no path to the actual
  // feature at all. Milan is now reachable by every tier -- Free/Gold-over-
  // allowance users just get a ₹99 BuyCreditModal from handleCompute() on a
  // 402, instead of being blocked from the page entirely.

  return (
    <div className="page-wrap">
      <div className="container">

        {/* Header */}
        <div style={{ marginBottom:24 }}>
          <button className="btn btn-ghost btn-sm"
                  onClick={() => navigate('/dashboard')} style={{ marginBottom:8 }}>
            ← {t('nav.dashboard')}
          </button>
          <h2 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)', marginBottom:4 }}>
            💍 {t('nav.milan')}
          </h2>
          <p style={{ fontSize:13, color:'var(--ink3)' }}>
            {t('milan.subtitle')}
          </p>
        </div>

        {/* Chart selector */}
        <div className="card" style={{ marginBottom:20 }}>
          <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:16 }}>
            {t('milan.selectCharts')}
          </h3>
          {error && <div className="info-box info-red" style={{ marginBottom:12 }}>{error}</div>}

          {charts.length < 2 ? (
            <div className="info-box info-saffron">
              {t('milan.needTwoCharts')}{' '}
              <a href="/chart/new">{t('milan.createNewChart')}</a>
            </div>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div className="form-group">
                  <label className="form-label">{t('milan.boyGroom')}</label>
                  <select className="form-input" value={chart1}
                          onChange={e => setChart1(Number(e.target.value))}>
                    <option value="">{t('milan.selectChart')}</option>
                    {charts.map(c => (
                      <option key={c.id} value={c.id}>{c.name} — {c.dob}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('milan.girlBride')}</label>
                  <select className="form-input" value={chart2}
                          onChange={e => setChart2(Number(e.target.value))}>
                    <option value="">{t('milan.selectChart')}</option>
                    {charts.map(c => (
                      <option key={c.id} value={c.id}>{c.name} — {c.dob}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button className="btn btn-primary"
                        onClick={handleCompute}
                        disabled={loading || !chart1 || !chart2}>
                  {loading ? t('milan.computing') : t('milan.computeButton')}
                </button>
                {result && (
                  pdfUrl ? (
                    <button className="btn btn-secondary"
                            onClick={() => downloadFile(pdfUrl, pdfName)}>
                      ⬇ {t('reportPage.downloadPdf')}
                    </button>
                  ) : (
                    <button className="btn btn-secondary"
                            onClick={handleGeneratePDF}
                            disabled={pdfLoading}>
                      {pdfLoading ? t('milan.generatingPdf') : `📄 ${t('milan.exportPdf')}`}
                    </button>
                  )
                )}
              </div>
            </>
          )}
        </div>

        {/* Results */}
        {result && (
          <div>
            {/* Score banner */}
            <div style={{
              background:'var(--indigo)', color:'#fff', borderRadius:12,
              padding:'24px 28px', marginBottom:20, textAlign:'center',
            }}>
              <div style={{ fontSize:13, opacity:0.7, marginBottom:8 }}>
                {result.person1} ❤ {result.person2}
              </div>
              <div style={{
                fontSize:64, fontFamily:'var(--font-mono)', fontWeight:700,
                color: result.total_score >= 27 ? '#4ade80'
                     : result.total_score >= 18 ? '#fbbf24' : '#f87171',
                lineHeight:1,
              }}>
                {result.total_score}
              </div>
              <div style={{ fontSize:18, opacity:0.8, marginTop:4 }}>
                {t('milan.outOf')} {result.max_score} {t('milan.points')}
              </div>
              <div style={{
                display:'inline-block', marginTop:12, padding:'6px 20px',
                borderRadius:20, fontSize:15, fontWeight:600,
                background: result.total_score >= 27 ? '#059669'
                          : result.total_score >= 18 ? '#D97706' : '#DC2626',
              }}>
                {result.verdict}
              </div>
            </div>

            {/* 8 Koots grid */}
            <div className="card" style={{ marginBottom:16 }}>
              <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:16 }}>
                {t('milan.ashtakootAnalysis')}
              </h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {KOOT_ORDER.map(key => {
                  const koot = result.koots[key];
                  if (!koot) return null;
                  const score = koot.score;
                  const max   = koot.max;
                  const pct   = (score / max) * 100;
                  const boy   = koot.boy || koot.gana1 || koot.nadi1 || '—';
                  const girl  = koot.girl || koot.gana2 || koot.nadi2 || '—';
                  return (
                    <div key={key} style={{
                      padding:'12px 14px', borderRadius:10,
                      background:'var(--ivory)', border:'0.5px solid var(--parch2)',
                    }}>
                      <div style={{ display:'flex', justifyContent:'space-between',
                                    alignItems:'center', marginBottom:6 }}>
                        <div>
                          <div style={{ fontSize:13, fontWeight:600, color:'var(--indigo)' }}>
                            {KOOT_LABELS[key]}
                          </div>
                          <div style={{ fontSize:10, color:'var(--ink3)' }}>
                            {KOOT_MEANING[key]}
                          </div>
                        </div>
                        <div style={{ fontSize:18, fontWeight:700,
                                      color: scoreColor(score, max) }}>
                          {score}/{max}
                        </div>
                      </div>
                      {/* Score bar */}
                      <div style={{ height:4, background:'var(--parch2)', borderRadius:2 }}>
                        <div style={{
                          height:4, borderRadius:2, width:`${pct}%`,
                          background: scoreColor(score, max), transition:'width 0.6s ease',
                        }}/>
                      </div>
                      <div style={{ fontSize:11, color:'var(--ink2)', marginTop:6 }}>
                        <span style={{ marginRight:8 }}>👦 {boy}</span>
                        <span>👧 {girl}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Doshas */}
            {result.doshas && result.doshas.length > 0 && (
              <div className="card" style={{ marginBottom:16,
                                             borderLeft:'3px solid var(--red)' }}>
                <h3 style={{ fontSize:14, color:'var(--red)', marginBottom:10 }}>
                  ⚠ {t('milan.doshasDetected')}
                </h3>
                {result.doshas.map((d, i) => (
                  <div key={i} style={{ marginBottom:8, padding:'8px 10px',
                                        background:'var(--ivory)', borderRadius:6 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--red)' }}>
                      {d.name}
                    </div>
                    <div style={{ fontSize:12, color:'var(--ink2)', marginTop:2 }}>
                      {t('milan.remedy')}: {d.remedy}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Mangal Dosha */}
            <div className="card" style={{ marginBottom:16 }}>
              <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:8 }}>
                {t('milan.mangalDoshaCheck')}
              </h3>
              <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:8 }}>
                <div style={{ fontSize:13 }}>
                  <strong>{result.person1}:</strong>{' '}
                  <span style={{ color: result.mangal_dosha.person1 ? 'var(--red)' : 'var(--teal)' }}>
                    {result.mangal_dosha.person1 ? `⚠ ${t('milan.mangalik')}` : `✓ ${t('milan.notMangalik')}`}
                  </span>
                </div>
                <div style={{ fontSize:13 }}>
                  <strong>{result.person2}:</strong>{' '}
                  <span style={{ color: result.mangal_dosha.person2 ? 'var(--red)' : 'var(--teal)' }}>
                    {result.mangal_dosha.person2 ? `⚠ ${t('milan.mangalik')}` : `✓ ${t('milan.notMangalik')}`}
                  </span>
                </div>
              </div>
              <p style={{ fontSize:13, color:'var(--ink2)', marginBottom:0 }}>
                {result.mangal_dosha.note}
              </p>
            </div>

            {/* AI Interpretation -- v4.2, BLUEPRINT.md Future Phases, Milan
                AI Enhancement. Narrative comes back already in the user's
                language (backend uses report_language), no new i18n key
                needed for the body text -- only the card title is a new
                translation key. */}
            {result.narrative && (
              <div className="card" style={{ marginBottom:16 }}>
                <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:8 }}>
                  {t('milan.aiInterpretation')}
                </h3>
                <p style={{ fontSize:13, color:'var(--ink2)', lineHeight:1.6, marginBottom:0 }}>
                  {result.narrative}
                </p>
              </div>
            )}

            {/* Summary */}
            <div className="card">
              <h3 style={{ fontSize:14, color:'var(--indigo)', marginBottom:12 }}>{t('milan.summary')}</h3>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)',
                            gap:10, marginBottom:16 }}>
                {[
                  { label:t('milan.totalScore'), val:`${result.total_score}/36` },
                  { label:t('milan.percentage'),  val:`${result.percentage}%` },
                  { label:t('milan.verdict'),     val: result.verdict },
                ].map(({ label, val }) => (
                  <div key={label} style={{ textAlign:'center', padding:'12px',
                                            background:'var(--sfll)', borderRadius:8 }}>
                    <div style={{ fontSize:10, color:'var(--ink3)', textTransform:'uppercase',
                                  letterSpacing:0.5, marginBottom:4 }}>{label}</div>
                    <div style={{ fontSize:15, fontWeight:600, color:'var(--indigo)' }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <button className="btn btn-secondary"
                        onClick={() => { setResult(null); setChart1(''); setChart2('');
                                         setPdfUrl(''); setPdfName(''); }}>
                  {t('milan.checkAnotherPair')}
                </button>
                {pdfUrl ? (
                  <button className="btn btn-primary"
                          onClick={() => downloadFile(pdfUrl, pdfName)}>
                    ⬇ {t('milan.downloadMilanPdf')}
                  </button>
                ) : (
                  <button className="btn btn-primary"
                          onClick={handleGeneratePDF} disabled={pdfLoading}>
                    {pdfLoading ? t('milan.generating') : `📄 ${t('milan.exportMilanPdf')}`}
                  </button>
                )}
                <button className="btn btn-ghost" onClick={() => navigate('/ask')}>
                  🔮 {t('milan.askAiDeeper')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <BuyCreditModal open={showBuyMilan} creditType="milan"
                      onClose={() => setShowBuyMilan(false)}
                      onPurchased={handleMilanPurchased}/>
    </div>
  );
}
